// ────────────────────────────────────────────────────────────────────────────
//  AgentBubble — consola flotante del agente de atención automatizado.
//
//  Es una burbuja arrastrable que vive encima del Centro de Mensajes: muestra en qué
//  estado está el agente (vigilando / trabajando / consultando / pausado), y al pasar
//  el cursor abre los controles. Tiene dos paneles que se despliegan desde ella:
//    · CONSULTAS — los casos que el agente redactó pero prefirió no mandar solo.
//      Se aprueban, se editan o se descartan. Si editás el texto, esa corrección se
//      guarda como ejemplo y el agente la usa la próxima vez.
//    · CONFIGURACIÓN — horario, ritmo humano, confianza, canales y reglas.
//
//  TODO el estado sale del backend (`/admin/inbox/agent/*`): la config vive en la base
//  porque el motor corre en el server y tiene que leerla — en localStorage sería por
//  navegador y el agente no la vería. Acá sólo queda la POSICIÓN de la burbuja, que sí
//  es una preferencia de cada pantalla.
//
//  El motor está en `tcdg-api`: InboxAgentService + el comando `inbox:agent-tick`.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { API_URI } from "../../helpers";

// ── Estados del agente ──────────────────────────────────────────────────────
// `engine` marca si el estado depende del motor de ejecución (que todavía no existe).
/**
 * Estados y su ÁNIMO.
 *
 * El color no es decoración suelta: cada estado tiene su propio humor y se nota en tres
 * cosas a la vez — la PALETA (los tres tonos del degradé), la VELOCIDAD a la que deriva y
 * lo INSISTENTE del latido. Dormido no respira; tranquilo va lento y frío; enfocado se
 * acelera y se satura; esperándote late corto y cálido, como alguien golpeando la mesa.
 *
 * `mood` es la clase de animación (ver useStyles) y `tri` los tres colores del degradé.
 */
const AGENT_STATE = {
  paused: {
    label: "En pausa",
    hint: "No mira la cola ni responde",
    animo: "dormido",
    color: "#94A3B8", accent: "#CBD5E1", glow: "rgba(148,163,184,0.35)",
    tri: ["#CBD5E1", "#94A3B8", "#64748B"],
  },
  idle: {
    label: "Vigilando",
    hint: "Esperando mensajes nuevos",
    animo: "tranquilo",
    color: "#22D3EE", accent: "#67E8F9", glow: "rgba(34,211,238,0.45)",
    // Fríos: cian, verde agua, celeste. Nada apura.
    tri: ["#67E8F9", "#34D399", "#38BDF8"],
  },
  working: {
    label: "Trabajando",
    hint: "Procesando la cola",
    animo: "enfocado",
    color: "#818CF8", accent: "#A5B4FC", glow: "rgba(129,140,248,0.6)",
    // Eléctricos: violeta, fucsia, cian. Es el estado con más energía.
    tri: ["#A5B4FC", "#F472B6", "#22D3EE"],
  },
  asking: {
    label: "Te consulta",
    hint: "Necesita una decisión tuya",
    animo: "esperandote",
    color: "#FBBF24", accent: "#FDE68A", glow: "rgba(251,191,36,0.6)",
    // Cálidos: ámbar, naranja, coral. Piden atención sin llegar al rojo de alarma.
    tri: ["#FDE68A", "#FB923C", "#F87171"],
  },
};

const DAYS = [
  { key: "mon", label: "L" }, { key: "tue", label: "M" }, { key: "wed", label: "M" },
  { key: "thu", label: "J" }, { key: "fri", label: "V" }, { key: "sat", label: "S" },
  { key: "sun", label: "D" },
];

const CHANNELS = [
  { key: "email", label: "Email" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "meta_fb", label: "Facebook" },
  { key: "meta_ig", label: "Instagram" },
];

// Temas que NUNCA se responden solos por más confianza que tenga: son escrituras
// sobre la cuenta del alumno o decisiones comerciales. Coinciden con los tres
// disparadores de escalamiento de docs/agente-inbox.md.
const ALWAYS_ASK = [
  { key: "pagos", label: "Pagos y activaciones", hint: "Asignar un pago, dar de alta, resetear claves" },
  { key: "bajas", label: "Bajas y reembolsos", hint: "Cancelaciones, devoluciones" },
  { key: "compensaciones", label: "Compensaciones", hint: "Regalar días o años de acceso" },
  { key: "hostiles", label: "Mensajes hostiles", hint: "Agresiones contra Mario o TCDG" },
];

// Los defaults de la config viven en el BACKEND (InboxAgentSetting::DEFAULTS): tener una
// segunda copia acá sólo garantiza que con el tiempo digan cosas distintas.

const POS_KEY = "tcdg.agent.pos.v1";

// ── API ─────────────────────────────────────────────────────────────────────
const AGENT = `${API_URI}/admin/inbox/agent`;

async function api(path, body) {
  const res = await fetch(AGENT + path, body
    ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
    : undefined);
  const json = await res.json().catch(() => null);
  if (!res.ok || !json || json.result === false) {
    throw new Error((json && json.error) || `Error ${res.status}`);
  }
  return json;
}

function loadPos() {
  try {
    const raw = window.localStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* noop */ }
  return null;
}

// Mismo breakpoint que el Centro de Mensajes.
const esCelular = () => window.innerWidth <= 900;
/** Mismo corte que `esCelular`, para los estilos. Si se separan, se desincronizan. */
const MOVIL = "@media (max-width: 900px)";

/**
 * Dónde aparece la burbuja la primera vez.
 *
 * En escritorio, abajo a la derecha. En CELULAR ahí está el cuadro de escribir la
 * respuesta y la burbuja se le sienta encima justo cuando hay que contestar; pero arriba
 * tampoco sirve, porque tapa los botones de la cabecera de la conversación. Queda pegada
 * al borde derecho a media altura, sobre el hilo —que scrollea— que es lo menos molesto
 * que hay en una pantalla chica. Igual se arrastra a donde uno quiera y ahí queda.
 */
function posInicial() {
  // En celular arrancaba al 45% del alto, que es exactamente donde están los mensajes: la
  // burbuja tapaba la conversación que hay que leer para contestar. Abajo a la derecha
  // molesta mucho menos, y se sigue pudiendo arrastrar a donde cada uno la quiera.
  return esCelular()
    ? { x: window.innerWidth - BUBBLE - 6, y: window.innerHeight - BUBBLE - 10 }
    : { x: window.innerWidth - 130, y: window.innerHeight - 190 };
}

// ────────────────────────────────────────────────────────────────────────────
//  Iconos (mismo set/estilo que Inbox.js: sólo stroke, 24×24)
// ────────────────────────────────────────────────────────────────────────────
const P = {
  play: "m6 3 14 9-14 9Z",
  pause: "M10 4v16 M15 4v16",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z",
  close: "M18 6 6 18 M6 6l12 12",
  check: "M20 6 9 17l-5-5",
  plus: "M5 12h14 M12 5v14",
  trash: "M3 6h18 M8 6V4h8v2 M19 6l-1 14H6L5 6",
  clock: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z M12 6v6l4 2",
  alert: "M12 9v4 M12 17h.01 M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z",
  minimize: "M5 12h14",
};

const Ico = ({ name, size = 15, color = "currentColor", w = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth={w} strokeLinecap="round" strokeLinejoin="round">
    <path d={P[name]} />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
//  Núcleo animado de la burbuja
//  Los anillos giran con SMIL (igual que el Spinner de Inbox.js: anda en Safari
//  sin depender de keyframes CSS) y los pulsos van por CSS.
// ────────────────────────────────────────────────────────────────────────────
const Core = ({ state, size = 44 }) => {
  const s = AGENT_STATE[state];
  const spin = state === "working" ? 2.2 : state === "asking" ? 3.4 : state === "idle" ? 7 : 0;
  const still = spin === 0;

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" style={{ display: "block" }}>
      <defs>
        {/* Varias paradas para que el anillo muestre 3-4 colores a la vez y la deriva
            de tono se note como un arcoíris girando, no como un color plano cambiando. */}
        {/* Los tres tonos del ÁNIMO. Que sean tres y no dos es lo que hace que el
            giro se lea como color moviéndose y no como un tinte plano. */}
        <linearGradient id={`ag-g-${state}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={s.tri[0]} />
          <stop offset="45%" stopColor={s.tri[1]} />
          <stop offset="100%" stopColor={s.tri[2]} stopOpacity="0.35" />
        </linearGradient>
        <linearGradient id={`ag-h-${state}`} x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={s.tri[2]} />
          <stop offset="50%" stopColor={s.tri[0]} />
          <stop offset="100%" stopColor={s.tri[1]} />
        </linearGradient>
      </defs>

      {/* Anillo exterior: arcos partidos girando */}
      <g opacity={still ? 0.35 : 0.95}>
        <circle cx="50" cy="50" r="38" stroke={`url(#ag-g-${state})`} strokeWidth="2.5"
                strokeLinecap="round" strokeDasharray="60 179" >
          {!still && (
            <animateTransform attributeName="transform" type="rotate"
                              from="0 50 50" to="360 50 50" dur={`${spin}s`} repeatCount="indefinite" />
          )}
        </circle>
        <circle cx="50" cy="50" r="38" stroke={s.color} strokeWidth="1" opacity="0.25" />
      </g>

      {/* Anillo medio: gira al revés y más lento */}
      <g opacity={still ? 0.3 : 0.85}>
        <circle cx="50" cy="50" r="27" stroke={`url(#ag-h-${state})`} strokeWidth="2.5"
                strokeLinecap="round" strokeDasharray="34 136">
          {!still && (
            <animateTransform attributeName="transform" type="rotate"
                              from="360 50 50" to="0 50 50" dur={`${spin * 1.9}s`} repeatCount="indefinite" />
          )}
        </circle>
      </g>

      {/* Partículas en órbita */}
      {!still && [0, 120, 240].map((deg, i) => (
        <g key={i}>
          <circle cx="50" cy="12" r={i === 0 ? 3.2 : 2.2}
                  fill={s.tri[i]} opacity={0.95 - i * 0.15}>
            <animateTransform attributeName="transform" type="rotate"
                              from={`${deg} 50 50`} to={`${deg + 360} 50 50`}
                              dur={`${spin * (1.3 + i * 0.45)}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}

      {/* Núcleo */}
      <circle cx="50" cy="50" r="13" fill={still ? s.color : `url(#ag-h-${state})`} opacity={still ? 0.35 : 0.95} />
      <circle cx="50" cy="50" r="13" fill="none" stroke={s.accent} strokeWidth="1.5" opacity="0.7" />
      {!still && (
        <circle cx="50" cy="50" r="13" fill="none" stroke={s.accent} strokeWidth="2" opacity="0.55">
          <animate attributeName="r" values="13;26;13" dur="2.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.55;0;0.55" dur="2.6s" repeatCount="indefinite" />
        </circle>
      )}
    </svg>
  );
};

// ────────────────────────────────────────────────────────────────────────────
//  Estilos
// ────────────────────────────────────────────────────────────────────────────
const BUBBLE = 74;

const useStyles = makeStyles({
  "@keyframes agFloat": {
    "0%,100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-5px)" },
  },
  "@keyframes agSheen": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  // Un juego de keyframes por ÁNIMO. La diferencia no es sólo el color: es cuánto se
  // mueve el tono y cuánto sube la saturación. Tranquilo apenas respira; enfocado barre
  // medio espectro y se satura; esperándote se queda en su franja cálida pero vibra.
  "@keyframes agAnimoTranquilo": {
    "0%, 100%": { filter: "hue-rotate(-18deg) saturate(1)" },
    "50%":      { filter: "hue-rotate(18deg) saturate(1.15)" },
  },
  "@keyframes agAnimoEnfocado": {
    "0%":   { filter: "hue-rotate(-55deg) saturate(1.25)" },
    "50%":  { filter: "hue-rotate(55deg) saturate(1.55)" },
    "100%": { filter: "hue-rotate(-55deg) saturate(1.25)" },
  },
  "@keyframes agAnimoEsperandote": {
    "0%, 100%": { filter: "hue-rotate(-12deg) saturate(1.3)" },
    "50%":      { filter: "hue-rotate(12deg) saturate(1.85)" },
  },
  // Halo detrás del orbe: late en tamaño y opacidad. Es lo que da el glow real —
  // el box-shadow solo se ve plano.
  "@keyframes agHalo": {
    "0%, 100%": { transform: "scale(0.92)", opacity: 0.55 },
    "50%":      { transform: "scale(1.18)", opacity: 0.95 },
  },
  "@keyframes agIn": {
    from: { opacity: 0, transform: "translateY(6px) scale(0.97)" },
    to: { opacity: 1, transform: "none" },
  },

  layer: {
    position: "fixed",
    zIndex: 1400,
    // El wrapper no debe capturar clicks fuera de sus hijos, si no tapa el inbox.
    pointerEvents: "none",
    fontFamily: "-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',Roboto,sans-serif",
  },
  stack: {
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    pointerEvents: "auto",
  },

  // ── Burbuja ──────────────────────────────────────────────
  bubbleWrap: {
    position: "relative",
    width: BUBBLE, height: BUBBLE,
    flexShrink: 0,
    animation: "$agFloat 5.5s ease-in-out infinite",
  },
  // Halo. Va DETRÁS del orbe (z-index negativo) y desbordando el contenedor.
  halo: {
    position: "absolute", inset: -18,
    borderRadius: "50%",
    pointerEvents: "none",
    zIndex: -1,
    // Late en tamaño; el período lo pone el ánimo (ver haloRitmo*), y va desfasado del
    // orbe a propósito para que se vean dos capas y no un bloque.
    animation: "$agHalo 4s ease-in-out infinite",
  },
  haloTranquilo:   { animationDuration: "5.5s" },
  haloEnfocado:    { animationDuration: "3s" },
  haloEsperandote: { animationDuration: "1.6s" },
  // En pausa no respira ni deriva: un agente apagado no debería verse "vivo".
  // La VELOCIDAD es la mitad del mensaje: lo mismo con otro tempo se lee distinto.
  animoTranquilo:   { animation: "$agAnimoTranquilo 15s ease-in-out infinite" },
  animoEnfocado:    { animation: "$agAnimoEnfocado 7s ease-in-out infinite" },
  animoEsperandote: { animation: "$agAnimoEsperandote 3.4s ease-in-out infinite" },
  bubbleWrapStill: { animation: "none" },
  bubble: {
    position: "relative",
    width: BUBBLE, height: BUBBLE,
    borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "grab",
    userSelect: "none",
    overflow: "hidden",
    // Orbe oscuro: sobre el fondo claro del inbox lee como "pantalla encendida".
    background: "radial-gradient(120% 120% at 30% 22%, #1E293B 0%, #0B1220 55%, #060B14 100%)",
    border: "1px solid rgba(255,255,255,0.10)",
    transition: "box-shadow .35s, transform .2s",
    touchAction: "none",
  },
  bubbleDragging: { cursor: "grabbing", transform: "scale(1.05)" },
  // Barrido de luz que da la sensación de superficie de vidrio.
  sheen: {
    position: "absolute", inset: -BUBBLE * 0.5,
    background: "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.10) 40deg, transparent 120deg)",
    animation: "$agSheen 9s linear infinite",
    pointerEvents: "none",
  },
  sheenStill: { animation: "none", opacity: 0.35 },
  glassTop: {
    position: "absolute", top: 4, left: 12, right: 12, height: "42%",
    borderRadius: "50%",
    background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0))",
    pointerEvents: "none",
  },

  // Badge con la cantidad en cola.
  queueBadge: {
    position: "absolute", top: -2, right: -2,
    minWidth: 21, height: 21, padding: "0 6px",
    borderRadius: 999,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 800, color: "#fff",
    border: "2px solid #F8FAFC",
    boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
    pointerEvents: "none",
  },

  // Ancla del chip: el chip es más ancho que la burbuja, así que se posiciona
  // absoluto sobre este contenedor para poder crecer hacia adentro de la pantalla.
  bubbleCol: { position: "relative", width: BUBBLE },

  // ── Chip de estado (lo que está haciendo) ────────────────
  statusChip: {
    position: "absolute",
    top: BUBBLE + 9,
    // En celular no va: colgado de una burbuja que ahora vive abajo se apoyaba sobre la
    // botonera del redactor, y no aporta nada que no diga ya el contador de la burbuja.
    // Lo que está preparando se ve tocándola, que es un toque igual que leer el chip.
    [MOVIL]: { display: "none" },
    display: "inline-flex", alignItems: "center", gap: 7,
    maxWidth: 230,
    padding: "5px 11px 5px 8px",
    background: "rgba(11,18,32,0.92)",
    backdropFilter: "blur(6px)",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.10)",
    boxShadow: "0 6px 18px rgba(15,23,42,0.22)",
    color: "#E2E8F0",
    fontSize: 11.5, fontWeight: 600, lineHeight: 1.25,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    animation: "$agIn .22s ease-out",
    pointerEvents: "none",
  },
  // Cuando hay algo en preparación el chip deja de ser decorativo: se puede abrir
  // para ver qué está redactando y para quién.
  statusChipClickable: {
    pointerEvents: "auto", cursor: "pointer",
    border: "1px solid rgba(255,255,255,0.22)",
    "&:hover": { background: "rgba(15,23,42,0.98)", borderColor: "rgba(255,255,255,0.35)" },
  },
  statusDot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },

  // ── Controles al hover ───────────────────────────────────
  controls: {
    display: "flex", flexDirection: "column", gap: 6,
    marginLeft: 8, marginTop: 6,
    animation: "$agIn .18s ease-out",
  },
  controlsLeft: { marginLeft: 0, marginRight: 8, alignItems: "flex-end" },
  ctrl: {
    width: 30, height: 30,
    display: "flex", alignItems: "center", justifyContent: "center",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(6px)",
    border: "1px solid #E2E8F0",
    boxShadow: "0 3px 10px rgba(15,23,42,0.12)",
    color: "#475569",
    cursor: "pointer",
    padding: 0,
    transition: "transform .12s, color .12s, border-color .12s",
    "&:hover": { transform: "scale(1.1)", color: "#0F172A", borderColor: "#CBD5E1" },
    "&:focus": { outline: "2px solid #A5B4FC", outlineOffset: 2 },
  },
  ctrlPrimary: {
    background: "#0F172A", borderColor: "#0F172A", color: "#fff",
    "&:hover": { transform: "scale(1.1)", color: "#fff", borderColor: "#0F172A" },
  },

  // ── Panel de configuración ───────────────────────────────
  panel: {
    width: 372,
    maxHeight: "min(78vh, 640px)",
    display: "flex", flexDirection: "column",
    marginLeft: 12,
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #E2E8F0",
    boxShadow: "0 18px 50px rgba(15,23,42,0.20)",
    overflow: "hidden",
    animation: "$agIn .2s ease-out",
    pointerEvents: "auto",
  },
  panelLeft: { marginLeft: 0, marginRight: 12 },

  // En el celular el panel NO puede ir al lado de la burbuja: burbuja (74) +
  // separación (12) + panel (368) son 454 px en una pantalla de 390, y se salía 72 px
  // por la izquierda. Acá se despega de la burbuja y toma la pantalla.
  panelMovil: {
    position: "fixed", left: 8, right: 8, top: 10, bottom: 10,
    width: "auto", maxWidth: "none", maxHeight: "none",
    margin: 0, zIndex: 1450,
  },
  // Velo detrás del panel: en pantalla chica el panel es modal de hecho, y sin velo
  // se confunde con la conversación de atrás.
  panelVelo: {
    position: "fixed", inset: 0, zIndex: 1440,
    background: "rgba(15,23,42,0.5)",
    pointerEvents: "auto",
  },
  panelHead: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "13px 14px",
    background: "linear-gradient(120deg, #0B1220, #1E293B)",
    color: "#F8FAFC",
    flexShrink: 0,
  },
  panelTitle: { fontSize: 13.5, fontWeight: 700, letterSpacing: -0.2, flex: 1 },
  panelSub: { fontSize: 10.5, fontWeight: 500, color: "#94A3B8", marginTop: 1 },
  panelX: {
    width: 26, height: 26, borderRadius: 8, border: "none",
    background: "rgba(255,255,255,0.08)", color: "#CBD5E1",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", padding: 0,
    "&:hover": { background: "rgba(255,255,255,0.16)", color: "#fff" },
  },
  panelBody: { overflowY: "auto", padding: "4px 14px 14px", flex: 1 },

  section: { paddingTop: 14 },
  sectionTitle: {
    fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6,
    color: "#94A3B8", marginBottom: 9,
  },
  row: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  label: { fontSize: 12.5, color: "#334155", fontWeight: 500 },
  hint: { fontSize: 11, color: "#94A3B8", lineHeight: 1.4, marginTop: 3 },

  dayRow: { display: "flex", gap: 5, marginBottom: 10 },
  day: {
    width: 30, height: 30, borderRadius: 9,
    border: "1px solid #E2E8F0", background: "#fff", color: "#94A3B8",
    fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0,
    transition: "all .12s",
    "&:hover": { borderColor: "#CBD5E1" },
  },
  dayOn: { background: "#0F172A", borderColor: "#0F172A", color: "#fff" },

  time: {
    width: 96, padding: "7px 9px",
    border: "1px solid #E2E8F0", borderRadius: 9,
    fontSize: 12.5, color: "#0F172A", fontFamily: "inherit",
    "&:focus": { outline: "none", borderColor: "#818CF8" },
  },
  num: {
    width: 62, padding: "7px 9px",
    border: "1px solid #E2E8F0", borderRadius: 9,
    fontSize: 12.5, color: "#0F172A", fontFamily: "inherit", textAlign: "center",
    "&:focus": { outline: "none", borderColor: "#818CF8" },
  },

  slider: {
    width: "100%", margin: "6px 0 2px",
    WebkitAppearance: "none", appearance: "none",
    height: 5, borderRadius: 999, background: "#E2E8F0", cursor: "pointer",
    "&::-webkit-slider-thumb": {
      WebkitAppearance: "none", appearance: "none",
      width: 17, height: 17, borderRadius: "50%",
      background: "#0F172A", border: "3px solid #fff",
      boxShadow: "0 1px 5px rgba(15,23,42,0.35)", cursor: "grab",
    },
    "&::-moz-range-thumb": {
      width: 17, height: 17, borderRadius: "50%",
      background: "#0F172A", border: "3px solid #fff", cursor: "grab",
    },
  },
  confHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between" },
  confVal: { fontSize: 19, fontWeight: 800, color: "#0F172A", letterSpacing: -0.5 },

  toggle: {
    position: "relative", width: 36, height: 20, borderRadius: 999,
    background: "#E2E8F0", border: "none", cursor: "pointer", padding: 0,
    flexShrink: 0, transition: "background .16s",
    "&:focus": { outline: "2px solid #A5B4FC", outlineOffset: 2 },
  },
  toggleOn: { background: "#0F172A" },
  knob: {
    position: "absolute", top: 2, left: 2,
    width: 16, height: 16, borderRadius: "50%", background: "#fff",
    boxShadow: "0 1px 3px rgba(15,23,42,0.3)",
    transition: "transform .16s",
  },
  knobOn: { transform: "translateX(16px)" },

  ruleItem: {
    display: "flex", alignItems: "flex-start", gap: 8,
    padding: "8px 9px", marginBottom: 6,
    background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 9,
    fontSize: 12, color: "#334155", lineHeight: 1.45,
  },
  ruleDel: {
    border: "none", background: "transparent", color: "#CBD5E1",
    cursor: "pointer", padding: 2, flexShrink: 0, marginTop: -1,
    "&:hover": { color: "#DC2626" },
  },
  ruleInput: {
    flex: 1, padding: "8px 10px",
    border: "1px solid #E2E8F0", borderRadius: 9,
    fontSize: 12, color: "#0F172A", fontFamily: "inherit", resize: "none",
    "&:focus": { outline: "none", borderColor: "#818CF8" },
  },
  addBtn: {
    width: 32, height: 32, borderRadius: 9, flexShrink: 0,
    border: "1px solid #E2E8F0", background: "#fff", color: "#475569",
    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
    "&:hover": { background: "#F1F5F9", color: "#0F172A" },
  },

  notice: {
    display: "flex", gap: 8, alignItems: "flex-start",
    margin: "14px 0 2px", padding: "9px 11px",
    background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10,
    fontSize: 11.5, color: "#92400E", lineHeight: 1.45,
  },

  panelFoot: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "11px 14px",
    borderTop: "1px solid #E2E8F0", background: "#F8FAFC", flexShrink: 0,
  },
  saved: { fontSize: 11.5, color: "#059669", fontWeight: 600, flex: 1 },
  savedIdle: { fontSize: 11.5, color: "#94A3B8", fontWeight: 500, flex: 1 },
  btn: {
    padding: "8px 15px", borderRadius: 9,
    border: "1px solid #E2E8F0", background: "#fff", color: "#475569",
    fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
    "&:hover": { background: "#F1F5F9" },
  },
  btnMain: {
    background: "#0F172A", borderColor: "#0F172A", color: "#fff",
    "&:hover": { background: "#1E293B" },
  },

  // ── Bandeja de consultas ─────────────────────────────────
  caseWrap: { padding: "12px 14px 4px" },
  caseHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  riskPill: {
    display: "inline-flex", alignItems: "center",
    padding: "2px 8px", borderRadius: 999,
    fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3,
  },
  confPill: {
    display: "inline-flex", alignItems: "center",
    padding: "2px 8px", borderRadius: 999,
    background: "#F1F5F9", color: "#475569",
    fontSize: 10.5, fontWeight: 800,
  },
  caseWho: { fontSize: 12.5, fontWeight: 700, color: "#0F172A", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  caseMsg: {
    background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10,
    padding: "9px 11px", fontSize: 12, color: "#334155", lineHeight: 1.5,
    maxHeight: 120, overflowY: "auto", whiteSpace: "pre-wrap", marginBottom: 10,
  },
  whyBox: {
    display: "flex", gap: 7, alignItems: "flex-start",
    background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10,
    padding: "8px 10px", fontSize: 11.5, color: "#92400E", lineHeight: 1.45, marginBottom: 10,
  },
  draftArea: {
    width: "100%", minHeight: 190, resize: "vertical",
    border: "1px solid #E2E8F0", borderRadius: 10, padding: "10px 11px",
    fontSize: 12.5, color: "#0F172A", fontFamily: "inherit", lineHeight: 1.55,
    "&:focus": { outline: "none", borderColor: "#818CF8" },
  },
  emptyCase: { padding: "34px 18px", textAlign: "center", color: "#94A3B8", fontSize: 12.5, lineHeight: 1.6 },
  nav: { display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#64748B", fontWeight: 600 },
  navBtn: {
    width: 24, height: 24, borderRadius: 7, border: "1px solid #E2E8F0",
    background: "#fff", color: "#475569", cursor: "pointer", padding: 0,
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, lineHeight: 1,
    "&:disabled": { opacity: 0.4, cursor: "default" },
  },
  err: {
    margin: "0 14px 10px", padding: "8px 10px", borderRadius: 9,
    background: "#FEE2E2", border: "1px solid #FECACA", color: "#991B1B",
    fontSize: 11.5, lineHeight: 1.4,
  },
  prepItem: {
    padding: "11px 14px",
    borderBottom: "1px solid #F1F5F9",
  },
  prepTop: { display: "flex", alignItems: "center", gap: 7, marginBottom: 5 },
  prepWho: { fontSize: 12.5, fontWeight: 700, color: "#0F172A", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  prepWhen: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "2px 8px", borderRadius: 999,
    background: "#EEF2FF", color: "#4338CA",
    fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap",
  },
  prepAsk: {
    fontSize: 11.5, color: "#64748B", lineHeight: 1.45, marginBottom: 6,
    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
  },
  prepDraft: {
    background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 9,
    padding: "8px 10px", fontSize: 12, color: "#334155", lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden",
  },
  prepDraftFull: { WebkitLineClamp: "unset", display: "block", maxHeight: 260, overflowY: "auto" },
  prepActions: { display: "flex", gap: 6, marginTop: 7, alignItems: "center" },
  linkBtn: {
    border: "none", background: "transparent", padding: 0, cursor: "pointer",
    fontSize: 11.5, fontWeight: 700, color: "#4338CA", fontFamily: "inherit",
    "&:hover": { textDecoration: "underline" },
  },
  linkBtnMuted: { color: "#94A3B8", "&:hover": { color: "#DC2626", textDecoration: "underline" } },

  // ── Auditoría ────────────────────────────────────────────
  auditFiltros: {
    display: "flex", gap: 6, padding: "10px 14px 8px",
    borderBottom: "1px solid #F1F5F9", flexShrink: 0,
  },
  filtro: {
    padding: "4px 11px", borderRadius: 999,
    border: "1px solid #E2E8F0", background: "#fff", color: "#64748B",
    fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
    "&:hover": { borderColor: "#CBD5E1" },
  },
  filtroOn: { background: "#0F172A", borderColor: "#0F172A", color: "#fff" },

  auditFila: {
    display: "flex", gap: 9, alignItems: "flex-start",
    padding: "9px 14px", borderBottom: "1px solid #F8FAFC",
  },
  auditHora: {
    fontSize: 10.5, color: "#94A3B8", fontWeight: 600,
    fontVariantNumeric: "tabular-nums", flexShrink: 0, width: 42, paddingTop: 1,
  },
  // La barrita de color es lo que deja barrer la lista de un vistazo y ver quién
  // hizo qué sin leer: el agente en violeta, Victor en ámbar, los fallos en rojo.
  auditMarca: { width: 3, alignSelf: "stretch", borderRadius: 2, flexShrink: 0 },
  auditCuerpo: { flex: 1, minWidth: 0 },
  auditQue: { fontSize: 12.5, color: "#0F172A", lineHeight: 1.35 },
  auditQuien: { fontWeight: 700 },
  // Sin recorte: acá se lee lo que se dijo. El clamp a 2 líneas cortaba justo las
  // respuestas del agente, que son las largas — o sea las que uno quiere leer.
  auditDetalle: {
    fontSize: 11, color: "#64748B", lineHeight: 1.45, marginTop: 2,
    whiteSpace: "pre-wrap", wordBreak: "break-word",
  },

  // Las idas y vueltas por WhatsApp se dibujan como chat: leer una conversación en
  // formato de log es incómodo y se pierde quién dijo qué.
  chatFila: { display: "flex", padding: "3px 14px" },
  chatMio: { justifyContent: "flex-end" },
  chatBurbuja: {
    maxWidth: "82%", padding: "8px 11px", borderRadius: 14,
    fontSize: 12.5, lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word",
  },
  chatDeVictor: {
    background: "#4F46E5", color: "#fff", borderBottomRightRadius: 4,
  },
  chatDelAgente: {
    background: "#fff", color: "#0F172A", border: "1px solid #E2E8F0", borderBottomLeftRadius: 4,
  },
  chatHora: { fontSize: 9.5, opacity: 0.65, marginTop: 3, textAlign: "right" },
  auditTag: {
    display: "inline-block", marginLeft: 6, padding: "1px 6px", borderRadius: 5,
    fontSize: 10, fontWeight: 800, verticalAlign: "middle",
  },

  dryBadge: {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "2px 8px", borderRadius: 999,
    background: "rgba(251,191,36,0.18)", color: "#FDE68A",
    fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4,
  },
});


// ────────────────────────────────────────────────────────────────────────────
//  Subcomponentes de formulario
// ────────────────────────────────────────────────────────────────────────────
const Toggle = ({ on, onChange, label }) => {
  const c = useStyles();
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label}
            className={`${c.toggle} ${on ? c.toggleOn : ""}`}
            onClick={() => onChange(!on)}>
      <span className={`${c.knob} ${on ? c.knobOn : ""}`} />
    </button>
  );
};

const Section = ({ title, children }) => {
  const c = useStyles();
  return (
    <div className={c.section}>
      <div className={c.sectionTitle}>{title}</div>
      {children}
    </div>
  );
};

/**
 * Cómo se lee cada hecho de la auditoría.
 *
 * El texto está redactado desde el punto de vista de Victor —«aprobaste», «te
 * respondió»— porque el que lee esto es él, y una lista en tercera persona
 * («el operador aprobó») se lee como el log de otra persona.
 */
const ACCION = {
  decide:            { txt: "evaluó un mensaje",        actor: "agente" },
  envia:             { txt: "respondió solo",           actor: "agente" },
  responde_operador: { txt: "te respondió por WhatsApp", actor: "agente" },
  aprueba:           { txt: "aprobaste su respuesta",   actor: "victor" },
  corrige_y_envia:   { txt: "corregiste y se envió",    actor: "victor" },
  descarta:          { txt: "descartaste el caso",      actor: "victor" },
  pausa:             { txt: "lo pausaste",              actor: "victor" },
  reanuda:           { txt: "lo reanudaste",            actor: "victor" },
  mensaje:           { txt: "le escribiste",            actor: "victor" },
  envio_fallido:     { txt: "falló el envío",           actor: "sistema" },
};

const ACTOR_COLOR = { agente: "#818CF8", victor: "#F59E0B", sistema: "#EF4444" };

const ESTADO_TAG = {
  sent:         { label: "enviada", bg: "#D1FAE5", fg: "#065F46" },
  queued:       { label: "en cola", bg: "#EEF2FF", fg: "#4338CA" },
  needs_review: { label: "consulta", bg: "#FEF3C7", fg: "#92400E" },
  skipped:      { label: "descartada", bg: "#F1F5F9", fg: "#64748B" },
  discarded:    { label: "descartada", bg: "#F1F5F9", fg: "#64748B" },
  failed:       { label: "falló", bg: "#FEE2E2", fg: "#991B1B" },
};

const RISK = {
  pagos:          { label: "Pago / cuenta", bg: "#DBEAFE", fg: "#1E40AF" },
  bajas:          { label: "Baja / reembolso", bg: "#FEE2E2", fg: "#991B1B" },
  compensaciones: { label: "Compensación", bg: "#FEF3C7", fg: "#92400E" },
  hostiles:       { label: "Hostil", bg: "#FFE4E6", fg: "#9F1239" },
};

// ────────────────────────────────────────────────────────────────────────────
//  Componente principal
// ────────────────────────────────────────────────────────────────────────────
export default function AgentBubble() {
  const c = useStyles();

  const [status, setStatus] = useState(null);      // estado vivo del motor
  const [config, setConfig] = useState(null);      // config guardada
  const [draft, setDraft] = useState(null);        // borrador de config del panel
  const [view, setView] = useState(null);          // null | 'cases' | 'config'
  const [hover, setHover] = useState(false);
  // En una pantalla táctil NO existe el hover: los controles salían sólo con
  // onMouseEnter, así que en el celular tocar la burbuja no mostraba nada. Ahí el
  // toque hace de interruptor.
  const [tactil] = useState(() => {
    try { return window.matchMedia("(hover: none)").matches; } catch (e) { return false; }
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Bandeja de consultas
  const [cases, setCases] = useState([]);
  const [idx, setIdx] = useState(0);
  const [prep, setPrep] = useState([]);        // en preparación (queued)
  const [audit, setAudit] = useState([]);      // historial
  const cuerpoAudit = useRef(null);
  const [auditActor, setAuditActor] = useState("all");
  const [abierto, setAbierto] = useState(null); // qué borrador está expandido
  const [text, setText] = useState("");
  const [newRule, setNewRule] = useState("");
  const [justSaved, setJustSaved] = useState(false);

  // ── Posición y arrastre ───────────────────────────────────────────────────
  const [pos, setPos] = useState(() => loadPos() || posInicial());
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ active: false, dx: 0, dy: 0 });

  const clamp = useCallback((p) => ({
    x: Math.max(8, Math.min(p.x, window.innerWidth - BUBBLE - 8)),
    y: Math.max(8, Math.min(p.y, window.innerHeight - BUBBLE - 8)),
  }), []);

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    drag.current = { active: true, moved: false, dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    setDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      if (!drag.current.active) return;
      const p = clamp({ x: e.clientX - drag.current.dx, y: e.clientY - drag.current.dy });
      // Umbral de 4px: un toque nunca es perfectamente quieto, y sin esto cualquier
      // temblor del dedo se cuenta como arrastre y el toque no abre los controles.
      if (Math.abs(p.x - pos.x) > 4 || Math.abs(p.y - pos.y) > 4) drag.current.moved = true;
      setPos(p);
    };
    const up = () => {
      drag.current.active = false;
      setDragging(false);
      setPos((p) => {
        try { window.localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch (err) { /* noop */ }
        return p;
      });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [dragging, clamp, pos.x, pos.y]);

  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const onResize = () => {
      setVp({ w: window.innerWidth, h: window.innerHeight });
      setPos((p) => clamp(p));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clamp]);

  // En táctil no hay mouseleave: sin esto los controles quedan abiertos para siempre.
  useEffect(() => {
    if (!tactil || !hover || open) return;
    const fuera = (e) => {
      const capa = document.querySelector('[class*="-stack-"]');
      if (capa && !capa.contains(e.target)) setHover(false);
    };
    document.addEventListener("pointerdown", fuera);
    return () => document.removeEventListener("pointerdown", fuera);
  }, [tactil, hover, open]);

  // ── Estado vivo ───────────────────────────────────────────────────────────
  const refreshStatus = useCallback(() => {
    api("/status")
      .then((d) => { setStatus(d); setError(null); })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    refreshStatus();
    api("/settings").then((d) => { setConfig(d.config); setDraft(d.config); }).catch(() => {});
    // 20 s: suficiente para que la burbuja se sienta viva sin castigar la API.
    const t = setInterval(refreshStatus, 20000);
    return () => clearInterval(t);
  }, [refreshStatus]);

  const loadCases = useCallback(() => {
    api("/decisions", { state: "needs_review", per_page: 50 })
      .then((d) => { setCases(d.data || []); setIdx(0); })
      .catch((e) => setError(e.message));
  }, []);

  // El texto editable sigue al caso que se está mirando.
  useEffect(() => {
    const cs = cases[idx];
    setText(cs ? (cs.draft || "") : "");
  }, [cases, idx]);

  const state = status ? status.state : "paused";
  const meta = AGENT_STATE[state] || AGENT_STATE.paused;
  const statusText = status ? status.text : "Conectando…";
  const badge = status ? (status.needs_review || status.pending || 0) : 0;

  // ── Acciones ──────────────────────────────────────────────────────────────
  const toggleRun = () => {
    setBusy(true);
    api("/toggle", { enabled: !(status && status.enabled) })
      .then((d) => { setStatus(d); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  const loadPrep = useCallback(() => {
    api("/decisions", { state: "queued", per_page: 50 })
      .then((d) => setPrep(d.data || []))
      .catch((e) => setError(e.message));
  }, []);

  const loadAudit = useCallback((actor) => {
    api("/audit", { actor: actor || "all", per_page: 60 })
      // El endpoint devuelve del más nuevo al más viejo, que está bien para un log pero
      // al revés para una conversación: se invierte para poder leerla de arriba a abajo.
      .then((d) => {
        setAudit((d.data || []).slice().reverse());
        // Como se lee de arriba a abajo, lo último queda al fondo: hay que abrir ahí,
        // igual que cualquier chat. Sin esto arranca en lo más viejo.
        setTimeout(() => {
          const el = cuerpoAudit.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 60);
      })
      .catch((e) => setError(e.message));
  }, []);

  const openCases = () => { setView("cases"); loadCases(); };
  const openAudit = () => { setView("audit"); loadAudit(auditActor); };
  const openPrep = () => { setAbierto(null); setView("prep"); loadPrep(); };
  const openConfig = () => { setDraft(config); setView("config"); };

  const patch = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const patchIn = (g, k, v) => setDraft((d) => ({ ...d, [g]: { ...d[g], [k]: v } }));

  const saveConfig = () => {
    setBusy(true);
    api("/settings", { config: draft })
      .then((d) => {
        setConfig(d.config); setDraft(d.config); setError(null);
        setJustSaved(true); setTimeout(() => setJustSaved(false), 2200);
        refreshStatus();
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  const addRule = () => {
    const t = newRule.trim();
    if (!t) return;
    setDraft((d) => ({ ...d, rules: [...(d.rules || []), t] }));
    setNewRule("");
  };

  const decide = (accion) => {
    const cs = cases[idx];
    if (!cs) return;
    setBusy(true);
    const req = accion === "approve"
      ? api("/decision/approve", { id: cs.id, text })
      : api("/decision/discard", { id: cs.id });
    req
      .then(() => {
        setError(null);
        setCases((prev) => {
          const next = prev.filter((x) => x.id !== cs.id);
          setIdx((i) => Math.max(0, Math.min(i, next.length - 1)));
          return next;
        });
        refreshStatus();
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  // Enviar ya (sin esperar el ritmo humano) o descartar, desde la cola en preparación.
  const decidirPrep = (id, accion) => {
    setBusy(true);
    const item = prep.find((x) => x.id === id);
    const req = accion === "send"
      ? api("/decision/approve", { id, text: item ? item.draft : undefined })
      : api("/decision/discard", { id });
    req
      .then(() => { setError(null); setPrep((p) => p.filter((x) => x.id !== id)); refreshStatus(); })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  // "sale en 7 min" es más útil que una hora absoluta cuando la espera es corta.
  const cuando = (iso) => {
    if (!iso) return "sin hora";
    const min = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
    if (min <= 0) return "sale ahora";
    if (min < 60) return `sale en ${min} min`;
    return "sale " + new Date(iso).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false });
  };

  // ── Layout ────────────────────────────────────────────────────────────────
  const flip = pos.x > vp.w / 2;
  const anchor = flip
    ? { right: Math.max(0, vp.w - (pos.x + BUBBLE)), top: pos.y }
    : { left: pos.x, top: pos.y };
  // 372 fijo se sale de un celular de 390: se acota al ancho real menos los márgenes.
  const panelW = Math.min(372, vp.w - 24);
  // En celular el panel se despega de la burbuja (ver panelMovil), así que ni el
  // ancho ni el desplazamiento vertical calculados aplican.
  const movil = vp.w <= 900;
  const panelH = Math.min(vp.h * 0.78, 640);
  const panelOffset = Math.max(8 - pos.y, Math.min(0, vp.h - 8 - panelH - pos.y));

  const open = view !== null;
  const cs = cases[idx];
  const riskMeta = cs && cs.risk ? RISK[cs.risk] : null;

  return (
    <div className={c.layer} style={anchor}>
      <div
        className={c.stack}
        style={{ flexDirection: flip ? "row-reverse" : "row" }}
        // En táctil se ignora el hover: el celular dispara un mouseenter sintético al
        // tocar, que abría los controles, y el onClick los cerraba en el mismo toque —
        // el primer toque no hacía nada y el segundo sí. Ahí manda sólo el onClick.
        onMouseEnter={tactil ? undefined : () => setHover(true)}
        onMouseLeave={tactil ? undefined : () => setHover(false)}
      >
        {/* ── Burbuja ─────────────────────────────────────── */}
        <div className={c.bubbleCol}>
          <div className={`${c.bubbleWrap} ${(dragging || hover || open) ? c.bubbleWrapStill : ""}`}>
            {state !== "paused" && (
              <span
                className={`${c.halo} ${
                  meta.animo === "enfocado" ? c.haloEnfocado
                  : meta.animo === "esperandote" ? c.haloEsperandote
                  : c.haloTranquilo}`}
                style={{ background: `radial-gradient(circle, ${meta.glow} 0%, rgba(0,0,0,0) 68%)` }}
              />
            )}
            <div
              className={`${c.bubble} ${dragging ? c.bubbleDragging : ""} ${
                meta.animo === "enfocado" ? c.animoEnfocado
                : meta.animo === "esperandote" ? c.animoEsperandote
                : meta.animo === "tranquilo" ? c.animoTranquilo
                : ""}`}
              style={{ boxShadow: `0 10px 30px rgba(15,23,42,0.28), 0 0 0 1px rgba(255,255,255,0.05) inset, 0 0 ${state === "paused" ? 10 : 26}px ${meta.glow}` }}
              onPointerDown={onPointerDown}
              onClick={() => { if (tactil && !drag.current.moved) setHover((h) => !h); }}
              onDoubleClick={() => (open ? setView(null) : openConfig())}
              title={`${meta.label} (${meta.animo}) — ${meta.hint}. Arrastrá para mover, doble click para configurar.`}
              role="button"
              tabIndex={0}
              aria-label={`Agente de atención: ${meta.label}`}
            >
              <div className={`${c.sheen} ${dragging ? c.sheenStill : ""}`} />
              <Core state={state} size={46} />
              <div className={c.glassTop} />
            </div>

            {badge > 0 && (
              <span className={c.queueBadge} style={{ background: meta.color }}>
                {badge > 99 ? "99+" : badge}
              </span>
            )}
          </div>

          {/* Con un panel abierto el chip sobra —el encabezado del panel dice lo mismo—
              y encima se le monta arriba, porque los dos crecen hacia el mismo lado. */}
          {!open && (() => {
            // El click abre LO QUE EL CHIP DICE: si avisa que te consulta, abre las
            // consultas; si dice que está redactando, abre la cola en preparación.
            // Abrir otra cosa que la que anuncia es peor que no ser clickeable.
            const abrir = state === "asking" ? openCases
              : (status && status.queued > 0) ? openPrep
              : null;
            const pista = state === "asking" ? "Ver los casos que te consulta"
              : "Ver qué está redactando y para quién";
            return (
              <div
                className={`${c.statusChip} ${abrir ? c.statusChipClickable : ""}`}
                style={flip ? { right: 0 } : { left: 0 }}
                onClick={abrir || undefined}
                title={abrir ? pista : undefined}
                role={abrir ? "button" : undefined}
              >
                <span className={c.statusDot} style={{ background: meta.color, boxShadow: `0 0 7px ${meta.glow}` }} />
                {statusText}
              </div>
            );
          })()}
        </div>

        {/* ── Controles al hover ──────────────────────────── */}
        {hover && !open && (
          <div className={`${c.controls} ${flip ? c.controlsLeft : ""}`}>
            <button
              className={`${c.ctrl} ${status && status.enabled ? "" : c.ctrlPrimary}`}
              onClick={toggleRun}
              disabled={busy}
              title={status && status.enabled ? "Pausar el agente" : "Reanudar el agente"}
              aria-label={status && status.enabled ? "Pausar" : "Reanudar"}
            >
              <Ico name={status && status.enabled ? "pause" : "play"} size={14} />
            </button>

            <button
              className={c.ctrl}
              onClick={openCases}
              title={status && status.needs_review ? `${status.needs_review} caso(s) esperando tu decisión` : "Consultas del agente"}
              aria-label="Consultas"
              style={status && status.needs_review ? { borderColor: "#FBBF24", color: "#B45309" } : undefined}
            >
              <Ico name="alert" size={14} />
            </button>

            <button className={c.ctrl} onClick={openAudit} title="Historial: todo lo que hizo" aria-label="Historial">
              <Ico name="clock" size={15} />
            </button>

            <button className={c.ctrl} onClick={openConfig} title="Configurar" aria-label="Configurar">
              <Ico name="settings" size={15} />
            </button>

            <button className={c.ctrl} onClick={() => setHover(false)} title="Ocultar controles" aria-label="Minimizar">
              <Ico name="minimize" size={15} />
            </button>
          </div>
        )}

        {/* ── Panel ───────────────────────────────────────── */}
        {open && movil && (
          <div className={c.panelVelo} onClick={() => setView(null)} />
        )}
        {open && (
          <div
            className={`${c.panel} ${movil ? c.panelMovil : (flip ? c.panelLeft : "")}`}
            style={movil ? undefined : { marginTop: panelOffset, width: panelW }}
          >
            <div className={c.panelHead}>
              <div style={{ flex: 1 }}>
                <div className={c.panelTitle}>
                  {view === "cases" ? "Consultas del agente"
                    : view === "prep" ? "En preparación"
                    : view === "audit" ? "Historial"
                    : "Agente de atención"}
                </div>
                <div className={c.panelSub}>
                  {view === "cases"
                    ? "Redactó estas respuestas pero prefirió no mandarlas solo"
                    : view === "prep"
                    ? "Ya redactadas, esperando su turno para salir"
                    : view === "audit"
                    ? "Todo lo que hizo el agente y todo lo que decidiste vos"
                    : "Cómo responde, cuándo, y cuándo te pregunta"}
                </div>
              </div>
              {status && status.dry_run && (
                <span className={c.dryBadge} title="Está en modo simulación: evalúa y agenda, pero no envía nada">
                  Simulación
                </span>
              )}
              <button className={c.panelX} onClick={() => setView(null)} aria-label="Cerrar">
                <Ico name="close" size={14} />
              </button>
            </div>

            {error && <div className={c.err} style={{ marginTop: 10 }}>{error}</div>}

            {/* ═══ Bandeja de consultas ═══ */}
            {view === "cases" && (
              <>
                <div className={c.panelBody} style={{ padding: 0 }}>
                  {!cs && (
                    <div className={c.emptyCase}>
                      No hay nada esperando tu decisión.<br />
                      Cuando el agente dude, el caso aparece acá.
                    </div>
                  )}

                  {cs && (
                    <div className={c.caseWrap}>
                      <div className={c.caseHead}>
                        <span className={c.caseWho}>
                          {(cs.customer && (cs.customer.name || cs.customer.handle)) || "Cliente"}
                        </span>
                        {riskMeta && (
                          <span className={c.riskPill} style={{ background: riskMeta.bg, color: riskMeta.fg }}>
                            {riskMeta.label}
                          </span>
                        )}
                        {cs.confidence != null && (
                          <span className={c.confPill}>{Math.round(cs.confidence * 100)}%</span>
                        )}
                      </div>

                      {cs.customer && cs.customer.subject && (
                        <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 5 }}>
                          {cs.customer.subject}
                        </div>
                      )}

                      <div className={c.caseMsg}>
                        {(cs.customer && cs.customer.content) || "(sin texto)"}
                      </div>

                      {(cs.reason || (cs.blockers && cs.blockers.length > 0)) && (
                        <div className={c.whyBox}>
                          <span style={{ flexShrink: 0, marginTop: 1 }}><Ico name="alert" size={12} /></span>
                          <span>
                            <b>Por qué te consulta:</b> {cs.reason}
                            {cs.blockers && cs.blockers.length > 0 && (
                              <>
                                <br />
                                {cs.blockers.map((b, i) => <span key={i}>· {b}<br /></span>)}
                              </>
                            )}
                          </span>
                        </div>
                      )}

                      <div className={c.sectionTitle} style={{ marginBottom: 6 }}>
                        Respuesta propuesta — editala si hace falta
                      </div>
                      <textarea
                        className={c.draftArea}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                      />
                      <div className={c.hint} style={{ marginBottom: 10 }}>
                        Si la corregís, esa corrección se guarda como ejemplo y el agente la
                        aplica la próxima vez que aparezca un caso parecido.
                      </div>
                    </div>
                  )}
                </div>

                <div className={c.panelFoot}>
                  <div className={c.nav} style={{ flex: 1 }}>
                    {cases.length > 0 && (
                      <>
                        <button className={c.navBtn} onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}>‹</button>
                        <span>{idx + 1} / {cases.length}</span>
                        <button className={c.navBtn} onClick={() => setIdx((i) => Math.min(cases.length - 1, i + 1))} disabled={idx >= cases.length - 1}>›</button>
                      </>
                    )}
                  </div>
                  <button className={c.btn} onClick={() => decide("discard")} disabled={!cs || busy}>
                    Descartar
                  </button>
                  <button className={`${c.btn} ${c.btnMain}`} onClick={() => decide("approve")} disabled={!cs || busy}>
                    {busy ? "Enviando…" : "Aprobar y enviar"}
                  </button>
                </div>
              </>
            )}

            {/* ═══ En preparación ═══ */}
            {view === "prep" && (
              <>
                <div className={c.panelBody} style={{ padding: 0 }}>
                  {prep.length === 0 && (
                    <div className={c.emptyCase}>
                      No hay nada en preparación.<br />
                      Acá aparecen las respuestas ya redactadas mientras esperan su turno.
                    </div>
                  )}

                  {prep.map((x) => {
                    const cli = x.customer || {};
                    const rm = x.risk ? RISK[x.risk] : null;
                    const exp = abierto === x.id;
                    return (
                      <div key={x.id} className={c.prepItem}>
                        <div className={c.prepTop}>
                          <span className={c.prepWho}>{cli.name || cli.handle || "Cliente"}</span>
                          {rm && (
                            <span className={c.riskPill} style={{ background: rm.bg, color: rm.fg }}>{rm.label}</span>
                          )}
                          {x.confidence != null && (
                            <span className={c.confPill}>{Math.round(x.confidence * 100)}%</span>
                          )}
                          <span className={c.prepWhen}>{cuando(x.scheduled_at)}</span>
                        </div>

                        {/* Qué preguntó — el "para quién y por qué" de un vistazo */}
                        <div className={c.prepAsk}>
                          {cli.subject ? <b>{cli.subject} · </b> : null}
                          {cli.content || "(sin texto)"}
                        </div>

                        {/* Qué va a contestar */}
                        <div className={`${c.prepDraft} ${exp ? c.prepDraftFull : ""}`}>
                          {x.draft}
                        </div>

                        <div className={c.prepActions}>
                          <button className={c.linkBtn} onClick={() => setAbierto(exp ? null : x.id)}>
                            {exp ? "Ver menos" : "Ver completo"}
                          </button>
                          <span style={{ color: "#CBD5E1" }}>·</span>
                          <button className={c.linkBtn} disabled={busy} onClick={() => decidirPrep(x.id, "send")}>
                            Enviar ya
                          </button>
                          <span style={{ color: "#CBD5E1" }}>·</span>
                          <button className={`${c.linkBtn} ${c.linkBtnMuted}`} disabled={busy} onClick={() => decidirPrep(x.id, "discard")}>
                            Descartar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className={c.panelFoot}>
                  <span className={c.savedIdle} style={{ flex: 1 }}>
                    {prep.length} {prep.length === 1 ? "respuesta" : "respuestas"} esperando turno
                  </span>
                  <button className={c.btn} onClick={loadPrep} disabled={busy}>Actualizar</button>
                  <button className={`${c.btn} ${c.btnMain}`} onClick={() => setView(null)}>Cerrar</button>
                </div>
              </>
            )}

            {/* ═══ Historial ═══ */}
            {view === "audit" && (
              <>
                <div className={c.auditFiltros}>
                  {[["all", "Todo"], ["agente", "El agente"], ["victor", "Vos"]].map(([k, lbl]) => (
                    <button
                      key={k}
                      className={`${c.filtro} ${auditActor === k ? c.filtroOn : ""}`}
                      onClick={() => { setAuditActor(k); loadAudit(k); }}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>

                <div className={c.panelBody} style={{ padding: 0 }} ref={cuerpoAudit}>
                  {audit.length === 0 && (
                    <div className={c.emptyCase}>
                      Todavía no hay nada registrado.<br />
                      Acá va quedando cada decisión, con su motivo y quién la tomó.
                    </div>
                  )}

                  {audit.map((a) => {
                    // La charla por WhatsApp se dibuja como chat, no como línea de log:
                    // es lo que Victor va a querer releer y así se ve quién dijo qué.
                    if (a.action === "mensaje" || a.action === "responde_operador") {
                      const mio = a.action === "mensaje";
                      return (
                        <div key={a.id} className={`${c.chatFila} ${mio ? c.chatMio : ""}`}>
                          <div className={`${c.chatBurbuja} ${mio ? c.chatDeVictor : c.chatDelAgente}`}>
                            {a.detalle}
                            <div className={c.chatHora}>
                              {a.at ? new Date(a.at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false }) : ""}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    const acc = ACCION[a.action] || { txt: a.action, actor: a.actor };
                    const color = ACTOR_COLOR[a.actor] || "#94A3B8";
                    const est = a.meta && a.meta.estado ? ESTADO_TAG[a.meta.estado] : null;
                    const conf = a.meta && a.meta.confianza != null
                      ? Math.round(a.meta.confianza * 100) + "%" : null;
                    return (
                      <div key={a.id} className={c.auditFila}>
                        <span className={c.auditHora}>
                          {/* 24 h forzado: en es-AR el toLocaleTimeString devuelve
                              "06:18 p. m.", que no entra en la columna y desalinea la lista. */}
                          {a.at ? new Date(a.at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}
                        </span>
                        <span className={c.auditMarca} style={{ background: color }} />
                        <div className={c.auditCuerpo}>
                          <div className={c.auditQue}>
                            {acc.txt}
                            {a.quien && <> · <span className={c.auditQuien}>{a.quien}</span></>}
                            {est && (
                              <span className={c.auditTag} style={{ background: est.bg, color: est.fg }}>
                                {est.label}
                              </span>
                            )}
                            {conf && (
                              <span className={c.auditTag} style={{ background: "#F1F5F9", color: "#475569" }}>
                                {conf}
                              </span>
                            )}
                          </div>
                          {a.detalle && <div className={c.auditDetalle}>{a.detalle}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className={c.panelFoot}>
                  <span className={c.savedIdle} style={{ flex: 1 }}>
                    {audit.length} {audit.length === 1 ? "registro" : "registros"}
                  </span>
                  <button className={c.btn} onClick={() => loadAudit(auditActor)} disabled={busy}>
                    Actualizar
                  </button>
                  <button className={`${c.btn} ${c.btnMain}`} onClick={() => setView(null)}>Cerrar</button>
                </div>
              </>
            )}

            {/* ═══ Configuración ═══ */}
            {view === "config" && draft && (
              <>
                <div className={c.panelBody}>
                  <Section title="Horario de atención">
                    <div className={c.dayRow}>
                      {DAYS.map((d) => (
                        <button key={d.key} type="button"
                                className={`${c.day} ${draft.days[d.key] ? c.dayOn : ""}`}
                                onClick={() => patchIn("days", d.key, !draft.days[d.key])}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                    <div className={c.row}>
                      <span className={c.label}>Desde</span>
                      <input type="time" className={c.time} value={draft.from}
                             onChange={(e) => patch("from", e.target.value)} />
                      <span className={c.label}>hasta</span>
                      <input type="time" className={c.time} value={draft.to}
                             onChange={(e) => patch("to", e.target.value)} />
                    </div>
                    <div className={c.hint}>
                      Fuera de este horario no contesta: deja los mensajes en la cola. Un rango que
                      cruza la medianoche (22:00 → 06:00) también funciona.
                    </div>
                  </Section>

                  <Section title="Ritmo humano">
                    <div className={c.row}>
                      <span className={c.label}>Espera entre</span>
                      <input type="number" min="0" className={c.num} value={draft.delayMin}
                             onChange={(e) => patch("delayMin", e.target.value)} />
                      <span className={c.label}>y</span>
                      <input type="number" min="0" className={c.num} value={draft.delayMax}
                             onChange={(e) => patch("delayMax", e.target.value)} />
                      <span className={c.label}>min</span>
                    </div>
                    <div className={c.row}>
                      <span className={c.label}>Máximo</span>
                      <input type="number" min="1" className={c.num} value={draft.maxPerHour}
                             onChange={(e) => patch("maxPerHour", e.target.value)} />
                      <span className={c.label}>respuestas por hora</span>
                    </div>
                    <div className={c.hint}>
                      Contesta en un momento al azar dentro de esa ventana. Responder al segundo, o
                      veinte veces seguidas en un minuto, es lo que delata que no hay una persona.
                      {status && status.sent_last_hour != null && (
                        <> Llevás <b>{status.sent_last_hour}</b> en la última hora.</>
                      )}
                    </div>
                  </Section>

                  <Section title="Autonomía">
                    <div className={c.confHead}>
                      <span className={c.label}>Confianza para enviar solo</span>
                      <span className={c.confVal}>{draft.confidence}%</span>
                    </div>
                    <input type="range" min="50" max="100" step="5" className={c.slider}
                           value={draft.confidence}
                           onChange={(e) => patch("confidence", Number(e.target.value))} />
                    <div className={c.hint}>
                      Un revisor aparte puntúa cada borrador. Si llega a <b>{draft.confidence}%</b> o
                      más, el agente lo manda solo; por debajo te lo deja acá para que decidas.
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <div className={c.label} style={{ marginBottom: 7, fontWeight: 600 }}>
                        Consultarte siempre, sin importar la confianza
                      </div>
                      {ALWAYS_ASK.map((a) => (
                        <div key={a.key} className={c.row} style={{ alignItems: "flex-start" }}>
                          <Toggle on={!!draft.alwaysAsk[a.key]} label={a.label}
                                  onChange={(v) => patchIn("alwaysAsk", a.key, v)} />
                          <div style={{ flex: 1 }}>
                            <div className={c.label}>{a.label}</div>
                            <div className={c.hint} style={{ marginTop: 1 }}>{a.hint}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Section>

                  <Section title="Canales que atiende">
                    {CHANNELS.map((ch) => (
                      <div key={ch.key} className={c.row}>
                        <Toggle on={!!draft.channels[ch.key]} label={ch.label}
                                onChange={(v) => patchIn("channels", ch.key, v)} />
                        <span className={c.label}>{ch.label}</span>
                      </div>
                    ))}
                    <div className={c.hint}>
                      Los comentarios públicos de YouTube quedan siempre afuera: no se responden.
                    </div>
                  </Section>

                  <Section title="Reglas de respuesta">
                    {(draft.rules || []).map((r, i) => (
                      <div key={i} className={c.ruleItem}>
                        <span style={{ flex: 1 }}>{r}</span>
                        <button className={c.ruleDel} title="Quitar"
                                onClick={() => patch("rules", draft.rules.filter((_, j) => j !== i))}>
                          <Ico name="trash" size={13} />
                        </button>
                      </div>
                    ))}
                    <div style={{ display: "flex", gap: 7, marginTop: 8 }}>
                      <textarea
                        className={c.ruleInput} rows={2} value={newRule}
                        placeholder="Escribí una regla nueva…"
                        onChange={(e) => setNewRule(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addRule(); }
                        }}
                      />
                      <button className={c.addBtn} onClick={addRule} title="Agregar regla">
                        <Ico name="plus" size={15} />
                      </button>
                    </div>
                    <div className={c.hint} style={{ marginTop: 6 }}>
                      Estas reglas se le pasan al agente por encima de cualquier plantilla o ejemplo.
                    </div>
                  </Section>

                  {status && status.dry_run && (
                    <div className={c.notice}>
                      <span style={{ flexShrink: 0, marginTop: 1 }}><Ico name="alert" size={13} /></span>
                      <span>
                        <b>Modo simulación activo.</b> El agente evalúa, redacta y agenda, pero no
                        envía nada. Se desactiva desde la base (<code>dry_run</code>) cuando quieras
                        que empiece a responder de verdad.
                      </span>
                    </div>
                  )}
                </div>

                <div className={c.panelFoot}>
                  <span className={justSaved ? c.saved : c.savedIdle}>
                    {justSaved ? "✓ Guardado" : "Se aplica al guardar"}
                  </span>
                  <button className={c.btn} onClick={() => { setDraft(config); setView(null); }}>
                    Cancelar
                  </button>
                  <button className={`${c.btn} ${c.btnMain}`} onClick={saveConfig} disabled={busy}>
                    {busy ? "Guardando…" : "Guardar"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
